import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, AlertCircle, Zap } from 'lucide-react';

const SITE_COOKIE = 'fray_site_access';
const INVESTOR_COOKIE = 'fray_investor_access';

function getCookie(name) {
  const match = document.cookie.split('; ').find(c => c.startsWith(name + '='));
  return match ? match.split('=')[1] : null;
}

function setCookie(name, value, days = 30) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // #825 H10: SameSite=strict + Secure (when HTTPS) — these are purely
  // client-side gates, no cross-origin submission path, so strict is safe
  // and shuts down the (tiny) CSRF surface. `secure` is conditional so
  // local dev on http://localhost still works.
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? ';secure' : '';
  document.cookie = `${name}=${value};path=/;expires=${expires};samesite=strict${secure}`;
}

// #1255: passwords are validated server-side via POST /api/preview/check
// (no longer baked into the build via REACT_APP_SITE_PASSWORD). The
// previous client-side compare leaked the password into every public
// JS bundle and required a redeploy to rotate. Backend stores bcrypt
// hashes in `platform_secrets`, exposed via the Platform Infrastructure
// admin tab.
const GATE_DISABLED = process.env.REACT_APP_DISABLE_GATE === 'true';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

async function verifyPassword(kind, password) {
  try {
    const resp = await fetch(`${BACKEND_URL}/api/preview/check`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind, password }),
    });
    if (!resp.ok) return false;
    const data = await resp.json();
    return Boolean(data && data.ok);
  } catch (err) {
    return false;
  }
}

/**
 * Site-wide password gate.
 * Wraps the entire app. If the user hasn't entered the site password, show a prompt.
 */
export function SitePasswordGate({ children }) {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (GATE_DISABLED || getCookie(SITE_COOKIE) === 'granted') {
      setUnlocked(true);
    }
  }, []);

  if (unlocked) return children;

  return (
    <PasswordPrompt
      title="Preview Access"
      subtitle="This site is in preview. Enter the password to continue."
      cookieName={SITE_COOKIE}
      kind="site"
      onSuccess={() => setUnlocked(true)}
    />
  );
}

/**
 * Investor section password gate.
 * Wraps just the /investors route. Requires separate password on top of the site-wide gate.
 */
export function InvestorPasswordGate({ children }) {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (GATE_DISABLED || getCookie(INVESTOR_COOKIE) === 'granted') {
      setUnlocked(true);
    }
  }, []);

  if (unlocked) return children;

  return (
    <PasswordPrompt
      title="Investor Access"
      subtitle="This section contains confidential investment materials."
      cookieName={INVESTOR_COOKIE}
      kind="investor"
      onSuccess={() => setUnlocked(true)}
      variant="investor"
    />
  );
}

function PasswordPrompt({ title, subtitle, cookieName, kind, onSuccess, variant = 'site' }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    const ok = await verifyPassword(kind, password);
    setSubmitting(false);
    if (ok) {
      setCookie(cookieName, 'granted');
      onSuccess();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setError(false), 3000);
    }
  };

  const isInvestor = variant === 'investor';
  const accentColor = isInvestor ? '#00D4FF' : '#FF4D00';

  return (
    <div className="fixed inset-0 z-[100] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
      {/* Subtle grain texture */}
      <div className="grain-overlay absolute inset-0" />

      {/* Faint radial glow */}
      <div
        className="absolute w-[600px] h-[600px] rounded-full opacity-[0.04] blur-3xl"
        style={{ background: `radial-gradient(circle, ${accentColor}, transparent 70%)` }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="relative w-full max-w-sm mx-4"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <div
            className="w-8 h-8 flex items-center justify-center"
            style={{
              backgroundColor: accentColor,
              clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
            }}
          >
            <Zap className="w-4 h-4 text-[#0A0A0A]" />
          </div>
          <span className="text-xl font-bold tracking-tight font-display uppercase text-[#F5F1E8]">
            Fray
          </span>
        </div>

        {/* Gate card */}
        <motion.div
          animate={shake ? { x: [-8, 8, -6, 6, -3, 3, 0] } : {}}
          transition={{ duration: 0.4 }}
          className="bg-[#1E1E1E] border border-[rgba(245,241,232,0.08)] p-8"
        >
          <div className="flex items-center gap-2 mb-2">
            <Lock className="w-4 h-4" style={{ color: accentColor }} />
            <h2 className="text-sm font-mono tracking-wider uppercase" style={{ color: accentColor }}>
              {title}
            </h2>
          </div>

          <p className="text-xs text-[rgba(245,241,232,0.56)] mb-6 font-body leading-relaxed">
            {subtitle}
          </p>

          <form onSubmit={handleSubmit}>
            <div className="relative">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                autoFocus
                className="w-full bg-[#0A0A0A] border text-[#F5F1E8] px-4 py-3 text-sm font-mono placeholder:text-[rgba(245,241,232,0.24)] focus:outline-none transition-colors"
                style={{
                  borderColor: error ? '#A61E17' : 'rgba(245,241,232,0.08)',
                }}
                onFocus={(e) => {
                  if (!error) e.target.style.borderColor = accentColor;
                }}
                onBlur={(e) => {
                  if (!error) e.target.style.borderColor = 'rgba(245,241,232,0.08)';
                }}
              />
            </div>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="flex items-center gap-1.5 mt-2"
                >
                  <AlertCircle className="w-3 h-3 text-[#A61E17]" />
                  <span className="text-xs font-mono text-[#A61E17]">Incorrect password</span>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              className="w-full mt-4 px-4 py-3 text-[#0A0A0A] font-display text-sm font-bold uppercase tracking-wider hover:opacity-90 active:opacity-80 transition-opacity"
              style={{ backgroundColor: accentColor }}
            >
              {isInvestor ? 'Access Investment Materials' : 'Enter'}
            </button>
          </form>
        </motion.div>

        {/* Bottom hint */}
        <p className="text-center mt-6 text-[10px] font-mono text-[rgba(245,241,232,0.24)] tracking-wider">
          {isInvestor
            ? 'Contact jim@getfray.ai for access'
            : 'Pre-launch preview — public access coming soon'
          }
        </p>
      </motion.div>
    </div>
  );
}
